import React, { useState, useEffect, useCallback } from 'react';
import * as Yup from 'yup';
import { useIsAdmin, useIsManager, useIsUser, useStore } from 'hooks';
import { ProfileService } from 'services';
import { VALIDATION, ROLE } from 'helpers/constants';
import Menu from 'components/menu';
import InfoColumn from './info-column';
import BalanceColumn from './balance-column';
import ChangePasswordColumn from './change-password-column';
import ChangePasswordByAdminColumn from './change-password-by-admin-column';
import styles from './info.module.scss';

const useFetchProfile = (userId) => {
  const { reload, setUser, setLoading } = useStore();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);

      const userProfileResponse = await new ProfileService({ setUser }).getProfile({ userId });
      if (userProfileResponse) {
        setProfile(userProfileResponse);
      }

      setLoading(false);
    })();
  }, [userId, reload, setUser, setLoading]);

  return profile;
};

const useInitialValues = ({ id, profile, contract }) => ({
  id,
  role: profile?.role?.code ?? '',
  okpo: profile?.okpo ?? '',
  name: profile?.name ?? '',
  shortName: profile?.shortName ?? '',
  uAddress: profile?.uAddress ?? '',
  pAddress: profile?.pAddress ?? '',
  oAddress: profile?.oAddress ?? '',
  bankName: profile?.bankName ?? '',
  personalAccount: profile?.personalAccount ?? '',
  bankAddress: profile?.bankAddress ?? '',
  email: profile?.email ?? '',
  fio: profile?.fio ?? '',
  phone: profile?.phone ?? '',
  balance: contract.balance || (profile?.contracts.length ? profile?.contracts[0].balance : '') || '',
  decadeLimit: Number(profile?.decadeLimit),
});

const InfoPage = ({
  match: {
    params: { userId },
  },
}) => {
  const isUser = useIsUser();
  const isAdmin = useIsAdmin();
  const isManager = useIsManager();
  const isProfile = Number(userId) >= 0;
  const isNotProfile = !isProfile;
  const [selectedContract, setSelectedContract] = useState({});
  const profile = useFetchProfile(userId);
  const initialValues = useInitialValues({ userId, profile, contract: selectedContract });
  const isUserProfile = useIsUser(profile?.role?.code);

  const validationSchema = useCallback(() => {
    const { id, balance, role: r, ...fields } = initialValues;

    return Yup.lazy(({ role }) => {
      switch (Number(role)) {
        case ROLE.USER:
          return Yup.object().shape({
            ...Object.keys(fields).reduce((prev, next) => {
              switch (next) {
                case 'role':
                  prev[next] = Yup.mixed().required();
                  break;
                case 'inn':
                  prev[next] = Yup.string()
                    .strict(true)
                    .trim(...VALIDATION.TRIM)
                    .test(...VALIDATION.SPACES_NOT_RESOLVED)
                    .required(...VALIDATION.REQUIRED);
                  break;
                case 'phone':
                  prev[next] = Yup.string()
                    .test(...VALIDATION.PHONE)
                    .required(...VALIDATION.REQUIRED);
                  break;
                case 'email':
                  prev[next] = Yup
                    .string()
                    .email(...VALIDATION.EMAIL)
                    .required(...VALIDATION.REQUIRED);
                  break;
                case 'decadeLimit':
                  prev[next] = Yup
                    .number()
                    .oneOf([0, 1])
                    .required(...VALIDATION.REQUIRED);
                  break;
                default:
                  prev[next] = Yup
                    .string()
                    .strict(true)
                    .trim(...VALIDATION.TRIM)
                    .required(...VALIDATION.REQUIRED);
              }
              return prev;
            }, {}),
          });
        case ROLE.ADMIN:
        case ROLE.MANAGER:
          return Yup.object({
            email: Yup
              .string()
              .email(...VALIDATION.EMAIL)
              .required(...VALIDATION.REQUIRED),
            name: Yup
              .string()
              .strict(true)
              .trim(...VALIDATION.TRIM)
              .required(...VALIDATION.REQUIRED),
          });
        default:
          return null;
      }
    });
  }, [initialValues]);

  useEffect(() => {
    if (profile && profile.contracts.length) {
      setSelectedContract(profile.contracts[0]);
    }
  }, [profile]);

  return (
    <>
      <div className={styles.menu}>
        <Menu />
      </div>
      <div className={styles.profile}>
        <InfoColumn
          styles={styles}
          profile={profile}
          setSelectedContract={setSelectedContract}
          initialValues={initialValues}
          validationSchema={validationSchema}
        />
        {isUserProfile
          ? <BalanceColumn styles={styles} contract={selectedContract} />
          : null}
        {isAdmin ? (
          <div className={styles.column}>
            <div className={`${styles.inner} ${styles.changePassword}`}>
              {isProfile ? (
                <ChangePasswordByAdminColumn />
              ) : (
                <ChangePasswordColumn />
              )}
            </div>
          </div>
        ) : null}
        {(isManager && isNotProfile) || isUser ? (
          <div className={styles.column}>
            <div className={`${styles.inner} ${styles.changePassword}`}>
              <ChangePasswordColumn />
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default InfoPage;
